import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import { ChevronLeft, Shield, Activity, KeyRound, User } from "lucide-react";
import ChangePasswordCard from "@/components/ChangePasswordCard";

/**
 * AdminSettings — a small home for admin-only self-service actions.
 * Currently: change my password, view my recent activity.
 */
export default function AdminSettings() {
  const { user } = useAuth();
  const [activity, setActivity] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    api
      .get("/admin/my-activity?limit=30")
      .then((r) => setActivity(r.data || []))
      .catch(() => setActivity([]))
      .finally(() => setLoading(false));
  }, []);

  return (
    <div className="space-y-6" data-testid="admin-settings-page">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <Link
            to="/admin"
            data-testid="admin-settings-back"
            className="inline-flex items-center gap-1 text-sm text-[color:var(--mano-ink-soft)] hover:text-[color:var(--mano-primary)]"
          >
            <ChevronLeft size={14} /> Back to admin dashboard
          </Link>
          <h1 className="font-display text-3xl mt-2">Admin settings</h1>
          <div className="uppercase-label inline-flex items-center gap-1 mt-1">
            <Shield size={12} /> Signed in as {user?.email}
          </div>
        </div>
      </div>

      <section
        data-testid="admin-settings-account"
        className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-6"
      >
        <div className="uppercase-label inline-flex items-center gap-1">
          <User size={14} /> Your admin identity
        </div>
        <dl className="mt-3 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm">
          <div>
            <dt className="text-[color:var(--mano-ink-soft)] text-xs uppercase tracking-wider">Name</dt>
            <dd className="mt-0.5 font-medium">{user?.name || "—"}</dd>
          </div>
          <div>
            <dt className="text-[color:var(--mano-ink-soft)] text-xs uppercase tracking-wider">Email</dt>
            <dd className="mt-0.5 font-mono text-xs break-all">{user?.email}</dd>
          </div>
          <div>
            <dt className="text-[color:var(--mano-ink-soft)] text-xs uppercase tracking-wider">Role</dt>
            <dd className="mt-0.5 capitalize">{(user?.role || "").replace("_", " ")}</dd>
          </div>
          <div>
            <dt className="text-[color:var(--mano-ink-soft)] text-xs uppercase tracking-wider">User ID</dt>
            <dd className="mt-0.5 font-mono text-xs break-all">{user?.id}</dd>
          </div>
        </dl>
      </section>

      <ChangePasswordCard />

      <section
        data-testid="admin-settings-activity"
        className="rounded-3xl bg-white border border-[color:var(--mano-border)] p-6"
      >
        <div className="uppercase-label inline-flex items-center gap-1">
          <Activity size={14} /> Your recent activity
        </div>
        <p className="text-sm text-[color:var(--mano-ink-soft)] mt-2 max-w-xl">
          The last 30 audit-trail entries you generated. Handy for
          reviewing your own actions or spotting anything unexpected.
        </p>
        {loading && (
          <div className="mt-4 text-sm text-[color:var(--mano-ink-soft)]">Loading…</div>
        )}
        {!loading && activity.length === 0 && (
          <div className="mt-4 rounded-2xl border border-dashed border-[color:var(--mano-border)] p-5 text-sm text-[color:var(--mano-ink-soft)]">
            No activity recorded yet.
          </div>
        )}
        {!loading && activity.length > 0 && (
          <ul className="mt-4 divide-y divide-[color:var(--mano-border)]">
            {activity.map((row) => (
              <li
                key={row.id}
                data-testid={`admin-activity-${row.id}`}
                className="py-3 flex items-center gap-3 flex-wrap"
              >
                <span
                  className="text-[10px] uppercase tracking-wider font-semibold px-2 py-1 rounded-full bg-[color:var(--mano-primary)]/10 text-[color:var(--mano-primary)]"
                  title={row.resource_id || ""}
                >
                  {row.action}
                </span>
                <div className="flex-1 min-w-0 text-sm">
                  <div className="text-[color:var(--mano-ink)]">
                    {row.resource_type}
                    {row.resource_id && (
                      <span className="ml-2 text-xs font-mono text-[color:var(--mano-ink-soft)]">
                        {String(row.resource_id).slice(0, 12)}
                      </span>
                    )}
                  </div>
                  <div className="text-xs text-[color:var(--mano-ink-soft)]">
                    {(row.at || "").replace("T", " ").slice(0, 19)} UTC
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </section>
    </div>
  );
}
